import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { t } from "@lingui/core/macro";
import { pick } from "ramda";

import { Link, TextLink } from "_/components/router";
import { Button, ButtonGroup } from "_/components/button";
import { FormTextField } from "_/components/text-field";
import { SubmitCancelButtons, useModal } from "_/components/modal";
import { ColumnDef, Table } from "_/components/table";
import { UuidTooltip } from "_/components/uuid-tooltip";
import { Checkbox } from "_/components/checkbox";
import { PermissionTest } from "_/components/permission-test";
import { Form } from "_/components/form";
import { FilterSelect } from "_/components/admin-view/filterSelect";

import { Org, useOrgs, useCreateOrg, useUpdateOrg } from "_/data/orgs";
import { ROOT_DOMAIN, permissions } from "_/data/rbac";

import { adminUrls } from "_/routes";

import { AdminPage } from "../admin-page";

import * as S from "../styled";

const InternalOrgCheckbox = ({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <S.CheckboxField>
    <Checkbox
      checked={checked}
      label={t`components.admin-view.internal-org`}
      onChange={onChange}
    />
    <S.CheckboxHint>{t`components.admin-view.internal-org-hint`}</S.CheckboxHint>
  </S.CheckboxField>
);

const OrgCreateForm = ({ onClose }: { onClose: () => void }) => {
  const {
    control,
    handleSubmit,
    formState: { dirtyFields },
  } = useForm({
    defaultValues: {
      name: "",
      makeCurrentUserMember: false,
      internal: false,
    },
  });

  const createOrg = useCreateOrg();

  const onSubmit = handleSubmit(async (data) => {
    await createOrg.mutateAsync(data);
    onClose?.();
  });

  return (
    <Form onSubmit={onSubmit}>
      <FormTextField
        control={control}
        name="name"
        rules={{
          required: t`components.admin-view.name-required`,
          minLength: {
            value: 2,
            message: t`components.admin-view.validate-org-name-length`,
          },
          maxLength: {
            value: 255,
            message: t`components.admin-view.validate-org-name-length`,
          },
        }}
        placeholder={t`components.projects-view.org-name`}
      />
      <Controller
        control={control}
        name="makeCurrentUserMember"
        render={({ field }) => (
          <Checkbox
            checked={field.value}
            label={t`components.admin-view.make-current-user-first-member`}
            onChange={field.onChange}
          />
        )}
      />
      <Controller
        control={control}
        name="internal"
        render={({ field }) => (
          <InternalOrgCheckbox
            checked={field.value}
            onChange={field.onChange}
          />
        )}
      />
      <SubmitCancelButtons
        submitButtonLabel={t`common.create`}
        onCancel={onClose}
        disableSubmit={!dirtyFields.name}
      />
    </Form>
  );
};

const OrgEditForm = ({ org, onClose }: { org: Org; onClose: () => void }) => {
  const pickFields = pick(["name", "internal"]);

  const {
    control,
    handleSubmit,
    formState: { isDirty },
  } = useForm({
    defaultValues: pickFields(org),
  });

  const updateOrg = useUpdateOrg();

  const onSubmit = handleSubmit(async (data) => {
    await updateOrg.mutateAsync({ id: org.id, update: data });
    onClose?.();
  });

  return (
    <Form onSubmit={onSubmit}>
      <FormTextField
        control={control}
        name="name"
        label={t`common.name`}
        placeholder={t`components.projects-view.org-name`}
        rules={{ required: t`components.admin-view.name-required` }}
      />
      <Controller
        control={control}
        name="internal"
        render={({ field }) => (
          <InternalOrgCheckbox
            checked={field.value}
            onChange={field.onChange}
          />
        )}
      />
      <SubmitCancelButtons onCancel={onClose} disableSubmit={!isDirty} />
    </Form>
  );
};

export const Organizations = () => {
  const { data: orgs = [], isLoading } = useOrgs();
  const createOrgModal = useModal<void>();
  const editOrgModal = useModal<Org>();
  const [orgFilter, setOrgFilter] = useState("");

  // Filter out organizations that the user does not have permission to view
  const filteredOrgs = orgs.filter(
    (org) =>
      org.name.toLowerCase().includes(orgFilter.toLowerCase()) &&
      org.permissions.includes(permissions.organizations.read)
  );
  const orgOptions = [
    ...new Set(filteredOrgs.map((o) => o.name).toSorted()),
  ].map((orgName) => ({
    label: orgName,
    value: orgName,
  }));

  const columns: ColumnDef<Org>[] = [
    {
      id: "name",
      header: t`components.settings-view.org-name`,
      accessorKey: "name",
      cell: ({ row: { original: rowData } }) => (
        <TextLink variant="default" to={adminUrls.organization(rowData.id)}>
          {rowData.name}
        </TextLink>
      ),
    },
    {
      id: "internal",
      header: t`components.admin-view.internal`,
      accessorKey: "internal",
      cell: ({ row: { original: rowData } }) =>
        rowData.internal ? t`common.enabled` : t`common.disabled`,
    },
    {
      id: "id",
      header: t`common.id`,
      accessorKey: "id",
      cell: ({ row: { original: rowData } }) => (
        <UuidTooltip uuid={rowData.id} />
      ),
    },
    {
      id: "actions",
      header: t`common.actions`,
      cell: ({ row: { original: rowData } }) => (
        <ButtonGroup>
          <Link to={adminUrls.organization(rowData.id)}>
            <Button size="small">{t`common.details`}</Button>
          </Link>
          <PermissionTest
            domainId={rowData.domainId}
            requiredPermissions={"organizations/write"}
            render={({ allowed }) => (
              <Button
                size="small"
                kind="secondary"
                onClick={() => editOrgModal.openModal(rowData)}
                disabled={!allowed}
              >
                {t`common.edit`}
              </Button>
            )}
          />
        </ButtonGroup>
      ),
    },
  ];

  const orgFilterSelect = (
    <S.OneFilterOrg>
      <FilterSelect
        name="orgFilter"
        options={orgOptions}
        placeholder={t`components.admin-view.filter.org`}
        filterValue={orgFilter}
        onFilterChange={setOrgFilter}
      />
    </S.OneFilterOrg>
  );

  return (
    <AdminPage
      title={t`components.admin-view.organizations`}
      headerControls={orgFilterSelect}
    >
      <S.AdminContentSection>
        <S.AdminContentSectionTitleContainer>
          <h2 />
          <PermissionTest
            domainId={ROOT_DOMAIN}
            requiredPermissions={"organizations/create"}
            render={({ allowed }) => (
              <Button
                onClick={() => createOrgModal.openModal(undefined)}
                disabled={!allowed}
              >{t`components.admin-view.create-org`}</Button>
            )}
          />
        </S.AdminContentSectionTitleContainer>
        <Table
          columns={columns}
          data={filteredOrgs}
          loading={isLoading}
          initialState={{ sorting: [{ id: "name", desc: false }] }}
        />
      </S.AdminContentSection>
      {createOrgModal.isModalOpen && (
        <createOrgModal.Modal title={t`components.admin-view.create-org`}>
          <OrgCreateForm onClose={createOrgModal.closeModal} />
        </createOrgModal.Modal>
      )}
      {editOrgModal.modalData && (
        <editOrgModal.Modal title={t`components.admin-view.edit-org`}>
          <OrgEditForm
            org={editOrgModal.modalData}
            onClose={editOrgModal.closeModal}
          />
        </editOrgModal.Modal>
      )}
    </AdminPage>
  );
};
